import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { Field, inputClass } from "../../components/forms/Field";
import FilterSelect from "../../components/forms/FilterSelect";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  Card,
  EmptyState,
  ErrorState,
  Panel,
  PanelToolbar,
  Refreshing,
  ROW_BTN,
  STALE,
  SearchInput,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import SegmentedControl from "../../components/timetable/SegmentedControl";
import ModalShell from "../Templates/ModalShell";
import ConfirmModal from "../Templates/ConfirmModal";
import { runErrorMessage } from "../../components/timetable/runErrors";
import {
  createScheduleConstraint,
  deleteScheduleConstraint,
  getActivities,
  getConstraintKinds,
  getScheduleConstraints,
  getTimetableSettings,
  updateScheduleConstraint,
} from "../../services/schedulingService";
import { getGroups } from "../../services/groupService";
import { getDomainSubjects } from "../../services/organizationService";
import { getUsers } from "../../services/userService";
import { WEEKDAY_KEY } from "../../utils/helpers/timetableDisplay";
import { compareText } from "../../utils/helpers/locale";

// Authored scheduling rules. EVERYTHING on this page is driven by
// GET /schedule-constraints/kinds/ — the kind list, which targets each kind
// accepts, and a jsonschema for its params. Nothing about the vocabulary is
// hardcoded here, so a kind added to the backend registry shows up (with a
// generated form) without a frontend change. The only knowledge this file has
// is how to render a *shape*: a bounded integer, a boolean, an enum, and the
// {slots: [{weekday, period}]} shape that deserves a week grid instead of raw
// JSON. Anything else falls back to a validated JSON textarea so an unknown
// future shape is still authorable.

// The four nullable target FKs, in the backend's reporting order
// (registry.TARGET_FIELDS). A rule targets AT MOST ONE of them; all blank means
// "the school-wide default for this kind" — which is why the picker is a radio
// group: two targets are not expressible in the UI at all.
const TARGET_FIELDS = ["teacher", "group", "subject", "activity"];

const HARD_WEIGHT = 100;
const DEFAULT_SOFT_WEIGHT = 70;

// Fallback week when the school has not saved timetable settings yet.
const FALLBACK_WEEKDAYS = [1, 2, 3, 4, 5];
const FALLBACK_PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];


const humanizeKey = (key) => String(key).replace(/_/g, " ");

const personLabel = (row) => {
  const name = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim();
  return name || row?.username || row?.email || "—";
};

const activityLabel = (row) => {
  const group = row?.group?.code || row?.group?.name || "?";
  const subject = row?.subject?.name || row?.subject?.code || "?";
  return `${group} · ${subject}`;
};

// "Whole school" or the name of the one resource a rule narrows to. Defined at
// module level because BOTH the table and the search index need it, and two
// copies would mean a rule you can see is named "Ms Ito" but cannot find by
// typing "Ito".
//
// `targetsReady` is what stops a per-teacher rule reading "No longer available"
// for the second or two the four picker requests are in flight — the row is
// fine, the list it is looked up in has simply not arrived yet.
const targetTextOf = (row, targets, t, targetsReady = true) => {
  if (!row.target_type) return t("timetable.schoolWide");
  const uuid = row[row.target_type];
  const option = (targets[row.target_type] || []).find((item) => item.value === uuid);
  if (option) return option.label;
  return targetsReady ? t("timetable.unknownTarget") : "…";
};

const isSlotsSchema = (schema) =>
  schema?.type === "array" &&
  schema?.items?.type === "object" &&
  Boolean(schema.items.properties?.weekday) &&
  Boolean(schema.items.properties?.period);

// A blank-but-valid params object for a kind: enough that the form opens on
// something the schema recognises rather than on `undefined` inputs.
const defaultParams = (schema) => {
  const properties = schema?.properties || {};
  const params = {};
  Object.entries(properties).forEach(([name, prop]) => {
    if (isSlotsSchema(prop)) params[name] = [];
    else if (prop.type === "integer" || prop.type === "number") {
      // Deliberately blank, NOT prop.minimum. Several knobs have minimum 0
      // ("at most N lessons a day"), so pre-filling the minimum meant opening
      // the form and pressing Save created a hard, school-wide rule that
      // forbids all teaching — and both the client and the backend schema
      // consider it valid. An empty value forces an explicit choice.
      params[name] = "";
    } else if (prop.type === "boolean") params[name] = false;
    else if (prop.type === "string") params[name] = prop.enum?.[0] ?? "";
    else params[name] = null;
  });
  return params;
};

export default function ConstraintsPage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const orgUuid = user?.organizations?.[0]?.organization?.uuid;

  const [kinds, setKinds] = useState([]);
  const [constraints, setConstraints] = useState([]);
  const [settings, setSettings] = useState(null);
  const [targets, setTargets] = useState({ teacher: [], group: [], subject: [], activity: [] });
  // The four target pickers resolve independently of the rules themselves; until
  // they land, a rule that names a teacher has nothing to name them WITH.
  const [targetsReady, setTargetsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // -- filters (all client-side) ---------------------------------------------
  // The whole rule set is fetched in ONE page (`page_size: MAX_PAGE_SIZE`) the
  // way Rooms is, so filtering here costs no round trip — and a rule list is
  // read to answer "what have I already said about Ms Ito?", which a server
  // round trip per keystroke would answer more slowly and no better.
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [strengthFilter, setStrengthFilter] = useState("all");
  const debouncedSearch = useDebouncedValue(search, 200);

  const loadConstraints = useCallback(
    (signal) => {
      setIsRefreshing(true);
      return getScheduleConstraints({ params: { page_size: MAX_PAGE_SIZE }, signal })
        .then((data) => {
          const rows = Array.isArray(data) ? data : data?.results || [];
          setConstraints(rows);
          setLoadFailed(false);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          setLoadFailed(true);
          toast.error(runErrorMessage(err, t, t("timetable.constraintsLoadFailed")));
        })
        .finally(() => {
          if (!signal?.aborted) setIsRefreshing(false);
        });
    },
    [t],
  );

  // The vocabulary + the rules + the week the slot grid draws. One controller:
  // these are all "reference data for this page", fetched once on mount.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    Promise.all([
      getConstraintKinds({ signal })
        .then((data) => setKinds(Array.isArray(data) ? data : data?.results || []))
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          setLoadFailed(true);
          toast.error(runErrorMessage(err, t, t("timetable.kindsLoadFailed")));
        }),
      loadConstraints(signal),
      getTimetableSettings({ signal })
        .then((data) => setSettings(data))
        .catch(() => {
          // Non-fatal — the slot grid falls back to Mon–Fri × 8.
        }),
    ]).finally(() => {
      if (!signal.aborted) setIsLoading(false);
    });

    // Target pickers. Each is optional: a failure just leaves that target type
    // with an empty list, and the rule can still be authored school-wide. They
    // are awaited together only to flip `targetsReady` — until then the table
    // must not claim a target it simply has not looked up yet.
    Promise.all([
      getUsers({ params: { role: "teacher", page_size: MAX_PAGE_SIZE }, signal })
        .then((data) => {
          const rows = Array.isArray(data) ? data : data?.results || [];
          setTargets((current) => ({
            ...current,
            teacher: rows.map((row) => ({ value: row.uuid, label: personLabel(row) })),
          }));
        })
        .catch(() => {}),

      getGroups({ params: { organization: orgUuid, page_size: MAX_PAGE_SIZE }, signal })
        .then((data) => {
          const rows = Array.isArray(data) ? data : data?.results || [];
          setTargets((current) => ({
            ...current,
            group: rows.map((row) => ({ value: row.uuid, label: row.code || row.name })),
          }));
        })
        .catch(() => {}),

      getDomainSubjects({ params: { page_size: MAX_PAGE_SIZE }, signal })
        .then((data) => {
          const rows = Array.isArray(data) ? data : data?.results || [];
          setTargets((current) => ({
            ...current,
            subject: rows.map((row) => ({ value: row.uuid, label: row.name })),
          }));
        })
        .catch(() => {}),

      getActivities({ params: { page_size: MAX_PAGE_SIZE }, signal })
        .then((data) => {
          const rows = Array.isArray(data) ? data : data?.results || [];
          setTargets((current) => ({
            ...current,
            activity: rows.map((row) => ({ value: row.uuid, label: activityLabel(row) })),
          }));
        })
        .catch(() => {}),
    ]).finally(() => {
      if (!signal.aborted) setTargetsReady(true);
    });

    return () => controller.abort();
  }, [loadConstraints, orgUuid, t]);

  const kindByName = useMemo(
    () => Object.fromEntries(kinds.map((spec) => [spec.kind, spec])),
    [kinds],
  );

  // Grouped so the school-wide defaults and the per-teacher overrides of the
  // same rule sit together.
  const sortedConstraints = useMemo(
    () =>
      [...constraints].sort(
        // Not compareText: `kind` and `target_type` are internal enum codes,
        // never shown raw. They want a stable ASCII order, not a collation
        // that shifts with the UI language.
        (a, b) => a.kind.localeCompare(b.kind) || (a.target_type || "").localeCompare(b.target_type || ""),
      ),
    [constraints],
  );

  // The filter's options come from the rules that EXIST, not from the 17-kind
  // vocabulary. A filter whose options mostly return nothing is a worse list
  // than no filter: every school authors a handful of the kinds, so offering
  // all of them would bury the four that are actually in the table.
  const kindOptions = useMemo(() => {
    const seen = new Map();
    constraints.forEach((row) => {
      if (!seen.has(row.kind)) seen.set(row.kind, kindLabel(row.kind, kindByName[row.kind], t));
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => compareText(a.label, b.label));
  }, [constraints, kindByName, t]);

  // Same rule, one level up: "Teacher" is only offered once a per-teacher rule
  // exists. `none` is the school-wide default, which the backend's own
  // `?target=none` filter names the same way.
  const targetOptions = useMemo(() => {
    const present = new Set(constraints.map((row) => row.target_type || "none"));
    return [
      { value: "", label: t("timetable.allTargets") },
      ...(present.has("none") ? [{ value: "none", label: t("timetable.schoolWide") }] : []),
      ...TARGET_FIELDS.filter((type) => present.has(type)).map((type) => ({
        value: type,
        label: t(`timetable.target.${type}`),
      })),
    ];
  }, [constraints, t]);

  const isFiltered = Boolean(
    debouncedSearch || kindFilter || targetFilter || strengthFilter !== "all",
  );

  const clearFilters = useCallback(() => {
    setSearch("");
    setKindFilter("");
    setTargetFilter("");
    setStrengthFilter("all");
  }, []);

  // A rule is found by what it SAYS, which is three separate strings on the row
  // — the rule name, who it applies to, and its settings. Searching only the
  // kind would make "Ito" and "9B" unfindable, which are the two things anyone
  // actually scans this table for.
  const visibleConstraints = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return sortedConstraints.filter((row) => {
      if (kindFilter && row.kind !== kindFilter) return false;
      if (targetFilter === "none" && row.target_type) return false;
      if (targetFilter && targetFilter !== "none" && row.target_type !== targetFilter) {
        return false;
      }
      if (strengthFilter === "hard" && !row.is_hard) return false;
      if (strengthFilter === "soft" && row.is_hard) return false;
      if (!q) return true;
      return [
        kindLabel(row.kind, kindByName[row.kind], t),
        targetTextOf(row, targets, t, targetsReady),
        paramSummary(row.params, t),
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    });
  }, [
    sortedConstraints, debouncedSearch, kindFilter, targetFilter, strengthFilter,
    kindByName, targets, targetsReady, t,
  ]);

  const onDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteScheduleConstraint({ uuid: deleting.uuid });
      setDeleting(null);
      toast.success(t("timetable.constraintDeleted"));
      loadConstraints();
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.constraintDeleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  // Page wrapper, back link, WIDTH and permission guard live on
  // TimetableWorkspace. The dark hero block went with them: inside a tab panel
  // it repeated the header bar and the active tab, and pushed the actual rules
  // below the fold.
  //
  // The "add" button moved INTO PanelHeader's action slot, where Lessons and
  // Rooms put theirs; it used to sit on a second full-width row beneath the
  // header, so the first card of this panel started ~44px lower than on the
  // neighbouring tabs. The Must-vs-Prefer note stays — it is the legend for the
  // table's HARD/SOFT column, not a second subtitle — but it now reads at the
  // same size and colour as every other explanatory line in the workspace
  // instead of 11px slate-500 directly under a 12px slate-500 subtitle.
  return (
    <>
      <Panel>
        <PanelHeader
          title={t("timetable.constraintsTitle")}
          count={isLoading ? undefined : visibleConstraints.length}
          subtitle={t("timetable.constraintsSubtitle")}
        >
          <button
            type="button"
            disabled={kinds.length === 0}
            onClick={() => setEditing({})}
            className={BTN.primary}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            {t("timetable.addConstraint")}
          </button>
        </PanelHeader>

        <p className="max-w-2xl text-xs font-medium leading-relaxed text-slate-500">
          {t("timetable.constraintsHint")}
        </p>

        {/* The toolbar appears once there is enough here to need it. On a
            school with three rules, four filter controls would be more chrome
            than content — and every one of them would be a no-op.
            `|| isFiltered` because deleting rules can take the list back under
            the threshold WITH a filter still set, and hiding the controls then
            would strand someone looking at a subset with no way to clear it. */}
        {!isLoading && (sortedConstraints.length > 3 || isFiltered) && (
          <PanelToolbar>
            <SearchInput
              value={search}
              onChange={setSearch}
              ariaLabel={t("timetable.constraintsSearchAria")}
            />
            {kindOptions.length > 1 && (
              <FilterSelect
                options={[{ value: "", label: t("timetable.allRules") }, ...kindOptions]}
                value={kindFilter}
                onChange={setKindFilter}
                ariaLabel={t("timetable.filterRule")}
                placeholder={t("timetable.allRules")}
                minWidthClass="min-w-[15rem]"
                searchable={kindOptions.length > 8}
                searchPlaceholder={t("common.search")}
                emptyText={t("timetable.noConstraintMatches")}
              />
            )}
            {targetOptions.length > 2 && (
              <FilterSelect
                options={targetOptions}
                value={targetFilter}
                onChange={setTargetFilter}
                ariaLabel={t("timetable.filterAppliesTo")}
                placeholder={t("timetable.allTargets")}
                minWidthClass="min-w-[11rem]"
              />
            )}
            {/* Must vs Prefer is the column people scan for — a hard rule is
                the only thing that can make a run produce nothing at all — so
                it is a control that shows both states at once, not a dropdown
                you have to open to see which way it is set. */}
            <SegmentedControl
              options={["all", "hard", "soft"]}
              value={strengthFilter}
              onChange={setStrengthFilter}
              ariaLabel={t("timetable.filterStrength")}
              renderLabel={(option) =>
                option === "all" ? t("timetable.allStrengths") : t(`timetable.${option}`)
              }
            />
            {isFiltered && (
              <button type="button" onClick={clearFilters} className={BTN.quiet}>
                {t("common.clearFilters")}
              </button>
            )}
            {isRefreshing && !isLoading && (
              <span className="ml-auto">
                <Refreshing />
              </span>
            )}
          </PanelToolbar>
        )}

        {isLoading ? (
          <TableSkeleton
            rows={5}
            columns={["w-48", "w-32", "w-16", "w-56 hidden lg:block"]}
          />
        ) : loadFailed && constraints.length === 0 ? (
          <ErrorState
            title={t("timetable.constraintsLoadFailed")}
            onRetry={() => loadConstraints()}
          />
        ) : sortedConstraints.length === 0 ? (
          <EmptyState
            iconPath="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
            title={t("timetable.noConstraints")}
            hint={t("timetable.noConstraintsHint")}
          />
        ) : visibleConstraints.length === 0 ? (
          <EmptyState
            iconPath="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
            title={t("timetable.noConstraintMatches")}
            hint={t("timetable.noConstraintMatchesHint")}
            action={
              <button type="button" onClick={clearFilters} className={BTN.secondary}>
                {t("common.clearFilters")}
              </button>
            }
          />
        ) : (
          <div className={isRefreshing ? STALE : undefined} aria-busy={isRefreshing}>
            <ConstraintTable
              rows={visibleConstraints}
              kindByName={kindByName}
              targets={targets}
              targetsReady={targetsReady}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          </div>
        )}
      </Panel>

      {editing && (
        <ConstraintFormModal
          row={editing.uuid ? editing : null}
          kinds={kinds}
          targets={targets}
          settings={settings}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadConstraints();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          danger
          busy={isDeleting}
          title={t("timetable.deleteConstraintTitle")}
          body={t("timetable.deleteConstraintBody")}
          confirmLabel={t("timetable.delete")}
          onConfirm={onDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------
// List
// --------------------------------------------------------------------------

function kindLabel(kind, spec, t) {
  // A translation when we have one, the server's registry label otherwise —
  // that fallback is what lets a brand-new kind appear with no frontend change.
  return t(`timetable.kind.${kind}`, spec?.label || kind);
}

function paramSummary(params, t) {
  const entries = Object.entries(params || {});
  if (entries.length === 0) return t("timetable.noParams");
  return entries
    .map(([key, value]) => {
      const label = t(`timetable.param.${key}`, humanizeKey(key));
      if (Array.isArray(value)) return `${label}: ${t("timetable.slotCount", { count: value.length })}`;
      if (typeof value === "boolean") return `${label}: ${value ? t("common.yes") : t("common.no")}`;
      return `${label}: ${value}`;
    })
    .join(" · ");
}

function ConstraintTable({ rows, kindByName, targets, targetsReady, onEdit, onDelete }) {
  const { t } = useTranslation();

  const targetText = (row) => targetTextOf(row, targets, t, targetsReady);

  return (
    <Card padded={false}>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className={TABLE_HEAD}>
            <tr>
              <th className="px-6 py-3">{t("timetable.colRule")}</th>
              <th className="px-6 py-3">{t("timetable.colAppliesTo")}</th>
              <th className="px-6 py-3">{t("timetable.colStrength")}</th>
              <th className="px-6 py-3 hidden lg:table-cell">{t("timetable.colSettingsCol")}</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.uuid} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-bold text-slate-900">
                  {kindLabel(row.kind, kindByName[row.kind], t)}
                </td>
                <td className="px-6 py-3">
                  {row.target_type ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        {t(`timetable.target.${row.target_type}`)}
                      </span>
                      <span className="font-bold text-slate-700">{targetText(row)}</span>
                    </span>
                  ) : (
                    <span className="text-slate-500 font-bold">{t("timetable.schoolWide")}</span>
                  )}
                </td>
                <td className="px-6 py-3">
                  {row.is_hard ? (
                    <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-widest">
                      {t("timetable.hard")}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest">
                      {t("timetable.softWithWeight", { weight: row.weight })}
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-slate-500 hidden lg:table-cell">
                  {paramSummary(row.params, t)}
                </td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className={ROW_BTN.edit}
                  >
                    {t("timetable.edit")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(row)}
                    className={`ml-1 ${ROW_BTN.delete}`}
                  >
                    {t("timetable.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Authoring form — generated from the kind's jsonschema
// --------------------------------------------------------------------------

function ConstraintFormModal({ row, kinds, targets, settings, onClose, onSaved }) {
  const { t } = useTranslation();

  const [kind, setKind] = useState(row?.kind || kinds[0]?.kind || "");
  const [targetType, setTargetType] = useState(row?.target_type || "");
  const [targetUuid, setTargetUuid] = useState(row?.target_type ? row[row.target_type] : "");
  const [params, setParams] = useState(() => {
    const spec = kinds.find((item) => item.kind === (row?.kind || kinds[0]?.kind));
    return { ...defaultParams(spec?.params_schema), ...(row?.params || {}) };
  });
  const [weight, setWeight] = useState(row?.weight ?? HARD_WEIGHT);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const spec = kinds.find((item) => item.kind === kind);
  const schema = spec?.params_schema || {};
  const properties = schema.properties || {};
  const requiredParams = schema.required || [];
  const allowedTargets = spec?.allowed_targets || [];

  const weekdays = settings?.teaching_weekdays?.length
    ? settings.teaching_weekdays
    : FALLBACK_WEEKDAYS;
  const periods = settings?.periods?.length
    ? settings.periods.map((period) => period.period)
    : FALLBACK_PERIODS;

  const onKindChange = (nextKind) => {
    const nextSpec = kinds.find((item) => item.kind === nextKind);
    setKind(nextKind);
    // A new kind means a new params schema and possibly a target this kind
    // cannot carry — reset both rather than POSTing a shape the server rejects.
    setParams(defaultParams(nextSpec?.params_schema));
    if (targetType && !(nextSpec?.allowed_targets || []).includes(targetType)) {
      setTargetType("");
      setTargetUuid("");
    }
    setErrors({});
  };

  const onTargetTypeChange = (nextType) => {
    setTargetType(nextType);
    setTargetUuid("");
  };

  const setParam = (name, value) => {
    setParams((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!kind) next.kind = t("timetable.kindRequired");
    if (targetType && !targetUuid) next.target = t("timetable.targetRequired");

    Object.entries(properties).forEach(([name, prop]) => {
      const value = params[name];
      const isRequired = requiredParams.includes(name);
      if (isSlotsSchema(prop)) {
        const count = Array.isArray(value) ? value.length : 0;
        const min = prop.minItems ?? 0;
        if (isRequired && count < Math.max(min, 1)) next[name] = t("timetable.slotsRequired");
        return;
      }
      if (prop.type === "integer" || prop.type === "number") {
        if (value === "" || value === null || value === undefined || Number.isNaN(Number(value))) {
          if (isRequired) next[name] = t("timetable.valueRequired");
          return;
        }
        const numeric = Number(value);
        if (typeof prop.minimum === "number" && numeric < prop.minimum) {
          next[name] = t("timetable.valueRange", { min: prop.minimum, max: prop.maximum ?? "∞" });
        }
        if (typeof prop.maximum === "number" && numeric > prop.maximum) {
          next[name] = t("timetable.valueRange", { min: prop.minimum ?? 0, max: prop.maximum });
        }
        return;
      }
      if (isRequired && (value === "" || value === null || value === undefined)) {
        next[name] = t("timetable.valueRequired");
      }
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    // Coerce numerics: <input type="number"> hands back strings, and the
    // backend's jsonschema is strict about integer vs string.
    const payloadParams = {};
    Object.entries(properties).forEach(([name, prop]) => {
      const value = params[name];
      if (prop.type === "integer") payloadParams[name] = parseInt(value, 10);
      else if (prop.type === "number") payloadParams[name] = Number(value);
      else payloadParams[name] = value;
    });

    const body = { kind, weight: Number(weight), params: payloadParams };
    // At most one target: every other FK is explicitly nulled so editing a
    // per-teacher rule back down to the school-wide default actually clears it.
    TARGET_FIELDS.forEach((field) => {
      body[field] = field === targetType ? targetUuid : null;
    });

    setIsSaving(true);
    try {
      if (row?.uuid) await updateScheduleConstraint({ uuid: row.uuid, body });
      else await createScheduleConstraint({ body });
      toast.success(row?.uuid ? t("timetable.constraintUpdated") : t("timetable.constraintCreated"));
      onSaved();
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.constraintSaveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-2xl">
      <h3 className="text-lg font-bold text-slate-900 pr-8">
        {row?.uuid ? t("timetable.editConstraint") : t("timetable.addConstraint")}
      </h3>

      <form onSubmit={onSubmit} className="mt-5" noValidate>
        <div className="space-y-5 max-h-[62vh] overflow-y-auto pr-1 -mr-1">
          <Field label={t("timetable.rule")} error={errors.kind} required>
            <select
              value={kind}
              disabled={isSaving}
              onChange={(e) => onKindChange(e.target.value)}
              className={inputClass(errors.kind)}
            >
              {kinds.map((item) => (
                <option key={item.kind} value={item.kind}>
                  {kindLabel(item.kind, item, t)}
                </option>
              ))}
            </select>
          </Field>

          {/* At most one target — a radio group makes two structurally impossible. */}
          <fieldset>
            <legend className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
              {t("timetable.appliesTo")}
            </legend>
            <div className="flex flex-wrap gap-2">
              <TargetChip
                active={targetType === ""}
                disabled={isSaving}
                label={t("timetable.schoolWide")}
                onSelect={() => onTargetTypeChange("")}
              />
              {allowedTargets.map((type) => (
                <TargetChip
                  key={type}
                  active={targetType === type}
                  disabled={isSaving}
                  label={t(`timetable.target.${type}`)}
                  onSelect={() => onTargetTypeChange(type)}
                />
              ))}
            </div>
            <p className="mt-1.5 text-[11px] font-medium text-slate-500">
              {targetType ? t("timetable.targetHint") : t("timetable.schoolWideHint")}
            </p>

            {targetType && (
              // A type-ahead, not a native <select>. The `activity` target
              // lists one option per curriculum line — ~190 on a real school —
              // and picking "9B · Chemistry" out of that by scrolling a native
              // dropdown is the slowest interaction in the workspace. The
              // teacher list is 45 deep for the same reason.
              <div className="mt-2">
                <FilterSelect
                  options={[
                    { value: "", label: t("timetable.pickTarget") },
                    ...(targets[targetType] || []),
                  ]}
                  value={targetUuid || ""}
                  onChange={(value) => {
                    setTargetUuid(value);
                    setErrors((current) => ({ ...current, target: undefined }));
                  }}
                  disabled={isSaving}
                  ariaLabel={t(`timetable.target.${targetType}`)}
                  placeholder={t("timetable.pickTarget")}
                  minWidthClass="w-full"
                  className={
                    errors.target ? "rounded-xl ring-2 ring-rose-500/30" : undefined
                  }
                  searchable={(targets[targetType] || []).length > 8}
                  searchPlaceholder={t("common.search")}
                  emptyText={t("timetable.noTargetMatches")}
                />
                {errors.target && (
                  <p className="mt-1 text-xs text-rose-600" role="alert">
                    {errors.target}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          {Object.keys(properties).length === 0 ? (
            <p className="text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              {t("timetable.noParamsHint")}
            </p>
          ) : (
            Object.entries(properties).map(([name, prop]) => (
              <ParamControl
                key={name}
                name={name}
                schema={prop}
                value={params[name]}
                error={errors[name]}
                disabled={isSaving}
                required={requiredParams.includes(name)}
                weekdays={weekdays}
                periods={periods}
                bellSchedule={settings?.periods}
                onChange={(value) => setParam(name, value)}
              />
            ))
          )}

          <WeightControl
            value={weight}
            disabled={isSaving}
            onChange={setWeight}
            spec={spec}
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={BTN.quiet}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={BTN.primary}
          >
            {isSaving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function TargetChip({ active, disabled, label, onSelect }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onSelect}
      className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
        active
          ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
          : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50"
      }`}
    >
      {label}
    </button>
  );
}

// One params field, chosen by SHAPE not by kind name.
function ParamControl({
  name, schema, value, error, disabled, required, weekdays, periods, bellSchedule, onChange,
}) {
  const { t } = useTranslation();
  const label = t(`timetable.param.${name}`, humanizeKey(name));

  if (isSlotsSchema(schema)) {
    return (
      <Field label={label} error={error} required={required}>
        <SlotGrid
          value={Array.isArray(value) ? value : []}
          disabled={disabled}
          weekdays={weekdays}
          periods={periods}
          bellSchedule={bellSchedule}
          onChange={onChange}
        />
      </Field>
    );
  }

  if (schema.type === "integer" || schema.type === "number") {
    const hint =
      typeof schema.minimum === "number" || typeof schema.maximum === "number"
        ? t("timetable.valueRange", { min: schema.minimum ?? 0, max: schema.maximum ?? "∞" })
        : null;
    return (
      <Field label={label} error={error} required={required}>
        <input
          type="number"
          inputMode="numeric"
          min={schema.minimum}
          max={schema.maximum}
          step={schema.type === "integer" ? 1 : "any"}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          className={inputClass(error)}
        />
        {hint && <p className="mt-1 text-[11px] font-medium text-slate-500">{hint}</p>}
      </Field>
    );
  }

  if (schema.type === "boolean") {
    return (
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-xs font-bold text-slate-700">{label}</span>
      </label>
    );
  }

  if (schema.type === "string" && Array.isArray(schema.enum)) {
    return (
      <Field label={label} error={error} required={required}>
        <select
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass(error)}
        >
          {schema.enum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (schema.type === "string") {
    return (
      <Field label={label} error={error} required={required}>
        <input
          type="text"
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          className={inputClass(error)}
        />
      </Field>
    );
  }

  // Escape hatch: a shape this UI has no widget for still has to be authorable,
  // otherwise a newly-registered kind would be dead on arrival.
  return (
    <Field label={label} error={error} required={required}>
      <textarea
        rows={3}
        value={typeof value === "string" ? value : JSON.stringify(value ?? null)}
        disabled={disabled}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            onChange(e.target.value);
          }
        }}
        aria-invalid={Boolean(error)}
        className={`${inputClass(error)} font-mono text-xs`}
      />
      <p className="mt-1 text-[11px] font-medium text-slate-500">{t("timetable.rawJsonHint")}</p>
    </Field>
  );
}

// The {slots: [{weekday, period}]} shape as a clickable week — click to select,
// click again to clear. Same visual vocabulary as PeriodPicker: selected is
// indigo solid, selectable is white with an indigo hover.
function SlotGrid({ value, disabled, weekdays, periods, bellSchedule, onChange }) {
  const { t } = useTranslation();
  const selected = new Set(value.map((slot) => `${slot.weekday}-${slot.period}`));

  const timeFor = (period) => {
    const row = (bellSchedule || []).find((item) => item.period === period);
    return row?.start ? String(row.start).slice(0, 5) : null;
  };

  const toggle = (weekday, period) => {
    const key = `${weekday}-${period}`;
    if (selected.has(key)) {
      onChange(value.filter((slot) => `${slot.weekday}-${slot.period}` !== key));
    } else {
      onChange([...value, { weekday, period }]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <div className="min-w-[420px]">
          <div
            className="grid gap-1"
            style={{ gridTemplateColumns: `3.5rem repeat(${weekdays.length}, minmax(0, 1fr))` }}
          >
            <div />
            {weekdays.map((weekday) => (
              <div
                key={weekday}
                className="text-center text-[10px] font-black uppercase tracking-widest text-slate-500 pb-1"
              >
                {t(`timetable.weekday.${WEEKDAY_KEY[weekday]}`)}
              </div>
            ))}
            {periods.map((period) => (
              <FragmentRow
                key={period}
                period={period}
                time={timeFor(period)}
                weekdays={weekdays}
                selected={selected}
                disabled={disabled}
                onToggle={toggle}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-slate-500">
          {t("timetable.slotCount", { count: value.length })}
        </p>
        {value.length > 0 && !disabled && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-rose-600 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            {t("timetable.clearSlots")}
          </button>
        )}
      </div>
    </div>
  );
}

function FragmentRow({ period, time, weekdays, selected, disabled, onToggle }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex flex-col justify-center pr-1">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {t("timetable.periodShort", { period })}
        </span>
        {time && <span className="text-[9px] font-bold text-slate-500">{time}</span>}
      </div>
      {weekdays.map((weekday) => {
        const isOn = selected.has(`${weekday}-${period}`);
        return (
          <button
            key={weekday}
            type="button"
            disabled={disabled}
            aria-pressed={isOn}
            aria-label={`${t(`timetable.weekday.${WEEKDAY_KEY[weekday]}`)} ${t("timetable.periodShort", { period })}`}
            onClick={() => onToggle(weekday, period)}
            className={`h-9 rounded-lg border text-[10px] font-black transition disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${
              isOn
                ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
                : "bg-white border-slate-200 text-slate-300 hover:border-indigo-300 hover:bg-indigo-50"
            }`}
          >
            {isOn ? "✓" : ""}
          </button>
        );
      })}
    </>
  );
}

// weight 100 = hard (the solver MUST satisfy it); 0-99 = soft (a preference it
// trades off). Two different things, so the control is a mode switch first and
// a strength picker second — a bare 0-100 slider hides that 99 -> 100 is a
// change of kind, not of degree.
//
// The strength picker offers NAMED LEVELS rather than the raw 0-99 it used to.
// Two reasons, and the second is the serious one:
//
//  1. Nobody can answer "how important, from 0 to 99?". There were no anchors,
//     so the number was arbitrary and every school would have used a different
//     scale for the same intent.
//  2. The number you pick is NOT the number the solver uses. Each rule KIND
//     carries a `unit_cost` of 1-4, and the objective coefficient is
//     `weight * unit_cost` — so two rules both deliberately set to "70" are
//     traded off at 70 and 280. Three levels are honest about the granularity
//     that actually survives that multiplication, and `unit_cost` is surfaced
//     below rather than left as an invisible thumb on the scale.
const SOFT_LEVELS = [
  { key: "low", weight: 30 },
  { key: "medium", weight: 70 },
  { key: "high", weight: 95 },
];

// The authored value may be any legacy 0-99; snap it to the nearest level so an
// existing rule does not silently jump when the form is merely opened.
const nearestLevel = (value) =>
  SOFT_LEVELS.reduce((best, level) =>
    Math.abs(level.weight - Number(value)) < Math.abs(best.weight - Number(value))
      ? level
      : best,
  );

function WeightControl({ value, disabled, onChange, spec }) {
  const { t } = useTranslation();
  const isHard = Number(value) >= HARD_WEIGHT;
  const level = nearestLevel(value);
  const unitCost = Number(spec?.unit_cost) || 1;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
        {t("timetable.strength")}
      </p>
      <div className="flex gap-2">
        <TargetChip
          active={isHard}
          disabled={disabled}
          label={t("timetable.hard")}
          onSelect={() => onChange(HARD_WEIGHT)}
        />
        <TargetChip
          active={!isHard}
          disabled={disabled}
          label={t("timetable.soft")}
          onSelect={() => onChange(DEFAULT_SOFT_WEIGHT)}
        />
      </div>

      <p className="mt-2 text-[11px] font-medium text-slate-500 leading-relaxed">
        {isHard ? t("timetable.hardHint") : t("timetable.softHint")}
      </p>

      {!isHard && (
        <>
          <div
            role="radiogroup"
            aria-label={t("timetable.strength")}
            className="mt-3 grid grid-cols-3 gap-2"
          >
            {SOFT_LEVELS.map((option) => {
              const active = option.key === level.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => onChange(option.weight)}
                  className={`px-3 py-2 rounded-xl border text-[11px] font-black uppercase tracking-widest transition disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                    active
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-500/20"
                      : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300"
                  }`}
                >
                  {t(`timetable.priority.${option.key}`)}
                </button>
              );
            })}
          </div>
          {/* The invisible thumb on the scale, made visible. `unit_cost` is
              published by GET /kinds/ precisely so this can be said out loud. */}
          {unitCost > 1 && (
            <p className="mt-2 text-[11px] font-medium text-amber-700">
              {t("timetable.unitCostNote", { factor: unitCost })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
